import { useMemo, useState } from "react";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const [status, setStatus] = useState({ type: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  const initial = useMemo(
    () => ({
      name: "",
      email: "",
      phone: "",
      service: "Door Supervisors",
      message: "",
    }),
    [],
  );

  const [form, setForm] = useState(initial);

  function onChange(e) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ type: "idle", msg: "" });
    setLoading(true);

    try {
      // These must exist in Netlify env vars (or a local .env file)
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error(
          "Missing EmailJS env vars. Set REACT_APP_EMAILJS_SERVICE_ID / TEMPLATE_ID / PUBLIC_KEY.",
        );
      }

      // Template params must match your EmailJS template variables
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message,
      };

      await emailjs.send(serviceId, templateId, templateParams, { publicKey });

      setStatus({
        type: "success",
        msg: "Thanks, we’ll get back to you shortly.",
      });
      setForm(initial);
    } catch (err) {
      setStatus({
        type: "error",
        msg: err?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <section id="contact" className="border-t border-black/10 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid gap-10 md:grid-cols-2 md:items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Contact<span className="text-redAccent">.</span>
            </h2>
            <p className="mt-4 text-black/70 leading-relaxed">
              Tell us what you need coverage for, the date/time, location, and
              any specific requirements. We’ll respond with availability and a
              quote.
            </p>

            <div className="mt-8 rounded-3xl bg-ink text-white p-7">
              <div className="text-sm text-white/70">Direct details</div>
              <div className="mt-3 space-y-2 text-sm">
                <div>
                  <span className="text-white/70">Email:</span>{" "}
                  <span className="font-medium">
                    info@jwcyclonesecurity.co.uk
                  </span>
                </div>
                <div>
                  <span className="text-white/70">Phone:</span>{" "}
                  <span className="font-medium">07940053488</span>
                </div>
                <div>
                  <span className="text-white/70">Coverage:</span>{" "}
                  <span className="font-medium">
                    Manchester • North West • UK wide (as needed)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-black/10 bg-white p-7 shadow-soft">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-redAccent"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-redAccent"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@email.com"
                    type="email"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <input
                    className="mt-2 w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-redAccent"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+44..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Service</label>
                  <select
                    className="mt-2 w-full rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-redAccent bg-white"
                    name="service"
                    value={form.service}
                    onChange={onChange}
                  >
                    <option>Door Supervisors</option>
                    <option>Mobile Response</option>
                    <option>CCTV Operatives</option>
                    <option>Close Protection</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Message</label>
                <textarea
                  className="mt-2 w-full min-h-[140px] rounded-xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-redAccent"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us the venue/site, date/time, expected footfall, and any requirements..."
                  required
                />
              </div>

              {status.type !== "idle" && (
                <div
                  className={[
                    "rounded-xl px-4 py-3 text-sm border",
                    status.type === "success"
                      ? "bg-green-50 border-green-200 text-green-800"
                      : "bg-red-50 border-red-200 text-red-800",
                  ].join(" ")}
                >
                  {status.msg}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-ink text-white px-5 py-3 text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>

              <p className="text-xs text-black/55">
                By submitting, you consent to being contacted regarding this
                enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
